import { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon, KibChevronUpIcon } from '@chewy/kib-icons-react'
import * as R from 'ramda'
import useResizeObserver from 'use-resize-observer'
import type { Nil } from '@pbt/pbt-ui-components'

import type { IdentifiedAttachmentOrigin } from '~/types'

import MessageAttachment from './message-attachment/MessageAttachment'
import { getNumberOfHiddenItems } from './utils'

import styles from './AttachmentRow.module.scss'

const AttachmentRow = ({
  attachments,
  ariaLabelledBy,
}: {
  ariaLabelledBy: string
  attachments: IdentifiedAttachmentOrigin[]
}) => {
  const { t } = useTranslation(['Common', 'Communications'])
  const [hiddenIndices, setHiddenIndices] = useState<number[]>([])
  const [isExpanded, setIsExpanded] = useState(false)

  const updateHiddenIndices = (width: number | Nil) => {
    const hiddenItems = getNumberOfHiddenItems(attachments.length, width)
    setHiddenIndices(hiddenItems)
  }

  const { ref: rowRef, width: currentWidth } = useResizeObserver({
    onResize: ({ width }) => {
      updateHiddenIndices(width)
    },
  })

  useLayoutEffect(() => {
    updateHiddenIndices(currentWidth)
  }, [attachments.length])

  return (
    <div
      aria-labelledby={ariaLabelledBy}
      className={styles.attachmentRow}
      ref={rowRef}
      role="radiogroup"
    >
      <span className={styles.attachmentLabel} id={ariaLabelledBy}>
        {t('Common:ATTACHMENTS')}:
      </span>
      {attachments.map((attachment, index) => (
        <MessageAttachment
          attachment={attachment}
          choiceGroupName="attachment group"
          hidden={hiddenIndices.includes(index) && !isExpanded}
          key={attachment.id}
        />
      ))}
      {!R.isEmpty(hiddenIndices) && (
        <KibButtonNew
          aria-expanded={isExpanded}
          emphasis="tertiary"
          icon={isExpanded ? <KibChevronUpIcon /> : <KibChevronDownIcon />}
          iconPosition="end"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded
            ? t('Common:COLLAPSE_ACTION')
            : t('Communications:ATTACHMENT_ROW.MORE', {
                hiddenCount: hiddenIndices.length,
              })}
        </KibButtonNew>
      )}
    </div>
  )
}

export default AttachmentRow
